import type { CollectionElement, ItemProps, Node } from "@react-types/shared";
import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols";
import { flatten } from "ramda";
import { forwardRef, type HTMLAttributes, type Key, type PropsWithChildren, type ReactNode, useRef } from "react";
import {
  type AriaMenuOptions,
  DismissButton,
  FocusScope,
  mergeProps,
  OverlayContainer,
  useHover,
  useMenu,
  useMenuItem,
  useMenuSection,
  useSeparator,
} from "react-aria";
import { Item, Section, type TreeProps, type TreeState, useTreeState } from "react-stately";

import { DEPRECATED_useTooltip as useTooltip } from "../hooks/DEPRECATED_useTooltip.ts";
import { useDataAttributes } from "../hooks/useDataAttributes.ts";
import type { TooltipAsProp } from "../types/tooltip.d.ts";
import style from "./ContextualMenu/ContextualMenu.module.css";
import { SymbolIcon } from "./Icon/SymbolIcon.tsx";
import { Shortcut } from "./Shortcut/Shortcut.tsx";
import { TagIntent } from "./Tag/settings.ts";
import { Tag } from "./Tag/Tag.tsx";
import { TooltipIntent } from "./Tooltip/settings.ts";
import { Tooltip } from "./Tooltip/Tooltip.tsx";

// TODO: item.rendered should be what ?!?

/** @deprecated */
enum MenuItemIntent {
  Primary = "primary",
  Danger = "danger",
  Active = "active",
  Inactive = "inactive", // TODO: fix with disabled
}

interface MenuProps extends TreeProps<MenuSection>, AriaMenuOptions<MenuSection> {
  onAction?: (key: Key) => void;
  onClose?: () => void;
  domProps: HTMLAttributes<Element>;
  arrowProps?: HTMLAttributes<Element>;
  id?: string;
  header?: ReactNode;
  className?: string;
}

// TODO Fix interface
type Item = CollectionElement<MenuSectionProps>;
const getItems = (items: Item | Item[], recursive = true): MenuSection[] | undefined => {
  if (items == null) return undefined;

  const menuProps: unknown[] = [];

  function getItemProps(item: Item) {
    if (item?.props == null) return null;

    const { children, ...otherProps } = item.props; // just to extract children
    // first call is Section, then Item. That's why we keep the children at the end
    return recursive
      ? {
          ...otherProps,
          items: getItems((Array.isArray(children) ? flatten(children) : children) as Item | Item[], false),
        }
      : item.props;
  }

  if (Array.isArray(items)) {
    items
      .filter((i) => !!i) // remove falsy values (false, undefined, null...)
      .forEach((item) => {
        menuProps.push(getItemProps(item));
      });
  } else if (items) {
    menuProps.push(getItemProps(items));
  }

  return menuProps as MenuSection[];
};

const getAriaContextualMenuChildren = (section: MenuSection) => {
  return (
    <Section
      key={section.id || section.title}
      title={!section.hideTitle ? section.title : undefined}
      aria-label={section.title}
      items={section.items}
    >
      {({ children, ...props }) => (
        // FIXME Check with Key. See Tabs.
        //  The usage of id attribute are a bit "dangerous" as they need to be unique in the whole HTML page.
        <Item {...props} key={props.id || props.label} textValue={props.label || props.id}>
          {children}
        </Item>
      )}
    </Section>
  );
};

/**
 * To be used with the useMenu hook.
 * @deprecated Use ContextualMenu instead
 */
const Menu = forwardRef<HTMLDivElement, MenuProps>(function Menu(props, ref) {
  const { header, className, children, domProps, ...innerMenuProps } = props;

  // --- Menu ---
  const menuPropsWithSection = {
    ...innerMenuProps,
    // items and children are used instead of @react-aria Section and Item components.
    // See bellow to check how we abstract them through MenuSection and MenuItem components.
    items: getItems(children as Item | Item[]),
    children: getAriaContextualMenuChildren,
  };
  const state = useTreeState({ ...menuPropsWithSection, selectionMode: "none" });
  const menuRef = useRef<HTMLDivElement>(null);
  const { menuProps } = useMenu(menuPropsWithSection, state, menuRef);

  return (
    <OverlayContainer>
      <div
        className={clsx(style.menu, className)}
        {...domProps}
        ref={ref}
        id={props.id}
        data-testid={"contextual-menu"}
        style={{ ...domProps.style, overflow: props.header ? "hidden" : undefined }}
      >
        <FocusScope contain restoreFocus autoFocus>
          {/* TODO: This is the menu from here */}
          {!!header && (
            <div tabIndex={-1} className={style.header}>
              {header}
            </div>
          )}
          <div {...menuProps} ref={menuRef} className={style.menu_list}>
            {[...state.collection].map((item) => (
              <MenuSectionRenderer
                key={item.key}
                section={item}
                state={state}
                onAction={innerMenuProps.onAction}
                onClose={innerMenuProps.onClose}
              />
            ))}
          </div>
          <DismissButton onDismiss={innerMenuProps.onClose} />
        </FocusScope>
        {!!props.arrowProps && <div className={style.arrow} {...props.arrowProps} />}
      </div>
    </OverlayContainer>
  );
});

interface MenuSectionProps {
  section: Node<MenuSection>;
  state: TreeState<MenuSection>;
  onAction?: (key: Key) => void;
  onClose?: () => void;
}

// TODO: For the Select/ComboBox, this is the ListBox
function MenuSectionRenderer({
  section,
  state,
  onAction,
  onClose,
}: Pick<MenuSectionProps, "section" | "state" | "onAction" | "onClose">) {
  // perfect copy of useListBoxSection
  const { itemProps, headingProps, groupProps } = useMenuSection({
    heading: section.rendered,
    "aria-label": section["aria-label"],
  });

  const { separatorProps } = useSeparator({ elementType: "div" });

  // If the section is not the first, add a separator element.
  // The heading is rendered inside an <li> element, which contains
  // a <ul> with the child items.
  return (
    <>
      {section.key !== state.collection.getFirstKey() && <div {...separatorProps} className={style.separator} />}
      <div {...itemProps} className={style.section}>
        {!!section.rendered && (
          <span {...headingProps} className={style.category_title}>
            {section.rendered}
          </span>
        )}
        <div {...groupProps}>
          {[...section.childNodes].map((node) => (
            <MenuItemRenderer key={node.key} item={node} state={state} onAction={onAction} onClose={onClose} />
          ))}
        </div>
      </div>
    </>
  );
}

interface MenuItemProps {
  item: Node<MenuSection>;
  state: TreeState<MenuSection>;
  onAction?: (key: Key) => void;
  onClose?: () => void;
}

function MenuItemRenderer({
  item,
  state,
  onAction,
  onClose,
}: Pick<MenuItemProps, "item" | "state" | "onAction" | "onClose">) {
  const props = item.props as MenuItemProps;

  const dataAttributes = useDataAttributes(props);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isDisabled = state.disabledKeys.has(item.key ?? item.textValue);
  const isFocused = state.selectionManager.focusedKey === (item.key ?? item.textValue);
  const {
    menuItemProps,
    labelProps,
    descriptionProps,
    keyboardShortcutProps: _,
  } = useMenuItem(
    {
      key: item.key ?? item.textValue,
      isDisabled,
      onAction: action,
      onClose: props.keepOpen === true ? undefined : onClose,
    },
    state,
    triggerRef,
  );
  const {
    triggerProps,
    tooltipProps,
    arrowProps,
    state: tooltipState,
  } = useTooltip(triggerRef, tooltipRef, { placement: "right", isDisabled: !props.tooltip });

  function action(key: Key) {
    if (typeof onAction === "function") {
      onAction(key);
    }

    if (typeof props.onAction === "function") {
      props.onAction(key);
    }
  }

  const { hoverProps, isHovered } = useHover({ isDisabled });

  return (
    <>
      <div
        className={clsx(style.item, {
          [style.intent_primary]: props.intent === MenuItemIntent.Primary,
          [style.intent_active]: props.intent === MenuItemIntent.Active,
          [style.intent_danger]: props.intent === MenuItemIntent.Danger,
          [style.intent_inactive]: props.intent === MenuItemIntent.Inactive,
          [style.item_large]: props.isLarge,
          [style.no_style]: props.isOnlyContainer,
          [style.item_disabled]: isDisabled,
          [style.item_focus]: isHovered || isFocused,
        })}
        {...mergeProps(menuItemProps, triggerProps, hoverProps, dataAttributes)}
        ref={triggerRef}
        id={props.id}
      >
        <div className={style.things}>
          {!props.noIcon && (
            <div className={clsx(style.item_icon, { [style.item_icon_large]: props.isLarge })} aria-hidden>
              {props.intent === MenuItemIntent.Active ? (
                <SymbolIcon icon={"check"} />
              ) : props.iconImage ? (
                <img src={props.iconImage.path} width={props.iconImage.width} height={props.iconImage.height} alt="" />
              ) : props.icon ? (
                <SymbolIcon icon={props.icon} />
              ) : null}
            </div>
          )}
          <div className={style.title_wrapper}>
            <div
              {...labelProps}
              className={clsx(style.title, labelProps.className, {
                [style.item_label_title_large]: props.isLarge,
              })}
            >
              {/* textValue is set via props.label */}
              {item.rendered ?? item.textValue}
            </div>
            {!!props.description && (
              <div
                {...descriptionProps}
                className={clsx(style.subtitle, {
                  [style.item_label_subtitle_large]: props.isLarge,
                })}
              >
                {props.description}
              </div>
            )}
          </div>
        </div>
        <div className={style.things}>
          {!!props.tag && <Tag label={props.tag} intentColor={TagIntent.Outlined} />}
          {!!props.shortcut && <Shortcut label={props.shortcut} />}
        </div>
      </div>
      {!!tooltipState.isOpen && (
        <Tooltip
          domProps={tooltipProps}
          arrowProps={arrowProps}
          ref={tooltipRef}
          intent={props.intent === MenuItemIntent.Danger && !isDisabled ? TooltipIntent.Danger : TooltipIntent.Default}
        >
          {props.tooltip as ReactNode}
        </Tooltip>
      )}
    </>
  );
}

// --- Abstract @react-aria Section and Item ---
/** @deprecated */
interface MenuSection extends MenuSectionProps {
  items: PropsWithChildren<MenuItemProps>[];
}

interface MenuSectionProps {
  id: string; // also as the key prop, mandatory for @react-aria/Item, "title" is used if undefined
  title: string; // title or aria-label, mandatory for @react-aria/Section
  hideTitle?: boolean; // switch between title and aria-label
}

interface MenuItemProps extends Pick<ItemProps<unknown>, "aria-label"> {
  id: string; // also as the key prop, mandatory for @react-aria/Item
  label: string;
  /** Keep the menu open after the click */
  keepOpen?: boolean;
  tooltip?: TooltipAsProp | string;
  noIcon?: boolean; // TODO ?!? Should be bound to MenuDialog?
  icon?: MaterialSymbol;
  iconImage?: {
    path: string;
    width: number;
    height: number;
  };
  description?: ReactNode;
  tag?: string;
  shortcut?: string;
  onAction?: (key: Key) => void;
  intent?: MenuItemIntent;
  /** big icon, width 380px */
  isLarge?: boolean;
  /** no padding */
  isOnlyContainer?: boolean;
}

/** @deprecated */
function MenuSection(_props: PropsWithChildren<MenuSectionProps>) {
  // Only used to get props as parameters.
  // JSX is unused but children is!
  return null;
}

/** @deprecated */
function MenuItem(_props: PropsWithChildren<MenuItemProps>) {
  // Only used to get props as parameters.
  // JSX is unused but children is!
  return null;
}

export { Menu, MenuItem, MenuItemIntent, MenuSection };
